import { z } from 'zod';

/**
 * Wire protocol of the remote browser (apps/server/src/browser).
 *
 * The server drives a real Chromium and streams the active tab as JPEG frames over one WebSocket
 * (`GET /api/browser/ws`); the client renders the frames and forwards input. Nothing here names
 * Chromium, CDP or Playwright: a native-webview backend can implement the same messages.
 *
 * Conventions
 * - JSON text frames: `{ v: 1, t: <type>, ... }`. Binary frames: screencast images (see `encodeFrame`).
 * - Coordinates in input messages are CSS pixels of the client's viewport (0,0 = top-left of the image
 *   as displayed, 1 frame pixel = 1 client CSS px / dpr). The server divides by the tab's zoom.
 * - `tabId` is an opaque string chosen by the server; ids are stable for the life of a session.
 * - Every inbound message is validated; an invalid one gets `error { code: 'invalid_message' }`.
 * - WebSocket subprotocols: offer `omnia.browser.v1` and, when you cannot rely on the session cookie,
 *   `bearer.<token>` as a second entry (the token never goes in the URL). Alternatively send it in
 *   `hello.token`.
 */

export const BROWSER_PROTOCOL_VERSION = 1;
export const BROWSER_SUBPROTOCOL = 'omnia.browser.v1';

/** WebSocket close codes the server uses (application range 4000-4999). */
export const BROWSER_CLOSE = {
  replaced: 4001,
  unauthorized: 4401,
  guestNotAllowed: 4403,
  disabled: 4404,
  badHello: 4400,
  rateLimited: 4429,
  busy: 4503,
  sessionEnded: 4410,
} as const;

/** CDP-style modifier bitmask: alt = 1, ctrl = 2, meta = 4, shift = 8. */
export const MODIFIER = { alt: 1, ctrl: 2, meta: 4, shift: 8 } as const;

export const ZOOM_MIN = 0.25;
export const ZOOM_MAX = 5;
export const ZOOM_PRESETS = [0.5, 0.67, 0.75, 0.8, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2, 3] as const;

const v = z.literal(BROWSER_PROTOCOL_VERSION);
const tabId = z.string().min(1).max(32);
const requestId = z.string().min(1).max(64);
const coord = z.number().finite().min(-100_000).max(100_000);
const modifiers = z.number().int().min(0).max(15).default(0);
const url = z.string().min(1).max(8192);

const viewportFields = {
  /** CSS pixels of the area the client shows the page in. */
  w: z.number().int().min(100).max(8192),
  h: z.number().int().min(100).max(8192),
  dpr: z.number().min(0.5).max(5).default(1),
  /** True on touch devices: pages then see a touch screen and `input.touch` is used. */
  touch: z.boolean().optional(),
  /** JPEG quality 30-90; the server default (about 60) suits most pages. */
  quality: z.number().int().min(30).max(90).optional(),
};

// ---------------------------------------------------------------------------------------------
// Client -> server
// ---------------------------------------------------------------------------------------------

const mouseButton = z.enum(['none', 'left', 'middle', 'right', 'back', 'forward']);

export const browserClientMessage = z.discriminatedUnion('t', [
  /** First message. Creates the session or resumes the live one of this user. */
  z.object({
    v,
    t: z.literal('hello'),
    /** Only when the upgrade request carried no cookie, Authorization header or `bearer.` subprotocol. */
    token: z.string().min(1).max(512).optional(),
    viewport: z.object(viewportFields),
    /** Browser language and time zone, applied when a new session starts (e.g. "tr-TR", "Europe/Istanbul"). */
    locale: z.string().regex(/^[A-Za-z]{2,3}(-[A-Za-z0-9]{2,8})*$/).max(35).optional(),
    timezoneId: z.string().max(64).optional(),
  }),
  z.object({ v, t: z.literal('ping'), ts: z.number().finite().optional() }),
  z.object({ v, t: z.literal('viewport'), ...viewportFields }),

  // Tabs
  z.object({
    v,
    t: z.literal('tab.new'),
    /** Omitted: the home page. Typed text is normalised (search or https://), see `normalizeInput`. */
    url: url.optional(),
    /** Open without switching to it. */
    background: z.boolean().optional(),
  }),
  z.object({ v, t: z.literal('tab.close'), tabId }),
  z.object({ v, t: z.literal('tab.select'), tabId }),
  z.object({ v, t: z.literal('tab.reorder'), tabIds: z.array(tabId).min(1).max(64) }),

  // Navigation
  z.object({ v, t: z.literal('nav'), tabId, url }),
  z.object({ v, t: z.literal('back'), tabId }),
  z.object({ v, t: z.literal('forward'), tabId }),
  z.object({ v, t: z.literal('reload'), tabId }),
  z.object({ v, t: z.literal('stop'), tabId }),
  z.object({ v, t: z.literal('home'), tabId }),
  z.object({ v, t: z.literal('home.set'), url }),
  z.object({ v, t: z.literal('zoom'), tabId, factor: z.number().finite().min(ZOOM_MIN).max(ZOOM_MAX) }),

  // Input
  z.object({
    v,
    t: z.literal('input.mouse'),
    tabId,
    type: z.enum(['move', 'down', 'up']),
    x: coord,
    y: coord,
    button: mouseButton.default('none'),
    /** Buttons held after this event (DOM `MouseEvent.buttons`). */
    buttons: z.number().int().min(0).max(31).default(0),
    clickCount: z.number().int().min(0).max(3).default(1),
    modifiers,
  }),
  z.object({
    v,
    t: z.literal('input.wheel'),
    tabId,
    x: coord,
    y: coord,
    /** DOM `WheelEvent.deltaX/deltaY` in pixels (convert line/page deltas first). */
    dx: z.number().finite().min(-100_000).max(100_000),
    dy: z.number().finite().min(-100_000).max(100_000),
    modifiers,
  }),
  z.object({
    v,
    t: z.literal('input.key'),
    tabId,
    type: z.enum(['down', 'up']),
    /** DOM `KeyboardEvent.key` and `.code`. */
    key: z.string().min(1).max(32),
    code: z.string().max(32).default(''),
    /** DOM `KeyboardEvent.keyCode` (Windows virtual key code). */
    keyCode: z.number().int().min(0).max(255).default(0),
    /** The typed character, when the key produces one. Omit for ctrl/meta chords. */
    text: z.string().max(8).optional(),
    modifiers,
    repeat: z.boolean().optional(),
    location: z.number().int().min(0).max(3).optional(),
  }),
  /** Text from IME composition or dead keys: inserted at the caret as typed. */
  z.object({ v, t: z.literal('input.text'), tabId, text: z.string().min(1).max(10_000) }),
  z.object({
    v,
    t: z.literal('input.touch'),
    tabId,
    type: z.enum(['start', 'move', 'end', 'cancel']),
    /** All fingers currently down after this event (empty for the last `end`). */
    points: z
      .array(z.object({ id: z.number().int().min(0).max(1000), x: coord, y: coord, radius: z.number().min(0).max(500).optional(), force: z.number().min(0).max(1).optional() }))
      .max(10),
    modifiers,
  }),

  // Clipboard and context menu
  /** Paste: the client reads its clipboard (on Ctrl/Cmd+V or the menu) and sends the text. */
  z.object({ v, t: z.literal('clipboard.paste'), tabId, text: z.string().max(500_000) }),
  /** Where was the pointer? Answered by `ctx.result` so the client can build its own menu. */
  z.object({ v, t: z.literal('ctx.query'), tabId, requestId, x: coord, y: coord }),
  /** Ask for the current selection ("Copy" in the menu); answered by `copy.text`. */
  z.object({ v, t: z.literal('copy.request'), tabId, requestId: requestId.optional() }),

  // Pop-ups the page opens
  z.object({ v, t: z.literal('select.choose'), requestId, index: z.number().int().min(0).max(100_000).optional(), cancel: z.boolean().optional() }),
  z.object({ v, t: z.literal('dialog.reply'), dialogId: requestId, accept: z.boolean(), text: z.string().max(10_000).optional() }),
  z.object({
    v,
    t: z.literal('filechooser.reply'),
    requestId,
    /** Ids returned by `POST /api/browser/upload` or `/upload-from-files`. */
    uploadIds: z.array(z.string().min(1).max(64)).max(100).optional(),
    cancel: z.boolean().optional(),
  }),

  // Downloads
  z.object({ v, t: z.literal('download.cancel'), id: z.string().min(1).max(64) }),
  z.object({ v, t: z.literal('download.remove'), id: z.string().min(1).max(64) }),
  /** "Save image as" / "Save link as": the page's session downloads the URL. */
  z.object({ v, t: z.literal('save.url'), tabId, url }),
]);
export type BrowserClientMessage = z.infer<typeof browserClientMessage>;
export type BrowserClientMessageType = BrowserClientMessage['t'];

// ---------------------------------------------------------------------------------------------
// Server -> client
// ---------------------------------------------------------------------------------------------

export interface BrowserTab {
  id: string;
  /** Address bar text: the committed URL ("about:blank" for an empty tab). */
  url: string;
  title: string;
  /** Small data URL (at most 64 KB) or null. */
  favicon: string | null;
  loading: boolean;
  canGoBack: boolean;
  canGoForward: boolean;
  /** 1 = 100 %. Remembered per origin. */
  zoom: number;
  /** The renderer died; send `reload` to bring the tab back. */
  crashed: boolean;
  /** Restored from a previous session but not loaded yet; it loads on `tab.select`. */
  sleeping: boolean;
  /** Tab that opened this one (target=_blank, window.open). */
  openerTabId?: string;
}

export const DOWNLOAD_STATES = ['downloading', 'completed', 'failed', 'cancelled'] as const;
export type DownloadState = (typeof DOWNLOAD_STATES)[number];

export interface BrowserDownload {
  id: string;
  /** Sanitised file name (the name it will have in "İndirilenler"). */
  name: string;
  /** Host the file came from (never the full URL). */
  source: string;
  state: DownloadState;
  receivedBytes: number;
  /** Null when the server did not announce a length. */
  totalBytes: number | null;
  bytesPerSecond: number;
  etaSeconds: number | null;
  /** Machine code on failure: too_large, too_many_downloads, no_space, network, server_restarted, storage. */
  error?: string;
  /** The finished file in Omnia storage, to open, reveal or sign a link for. */
  fileId?: string;
  /** Folder it was saved to (the user's "İndirilenler"). */
  folderId?: string;
  startedAt: string;
  finishedAt?: string;
  /** Chromium cannot pause a download; always false. */
  canPause: false;
}

export interface BrowserLimits {
  maxTabs: number;
  maxDownloadBytes: number;
  maxUploadBytes: number;
  maxConcurrentDownloads: number;
  idleMinutes: number;
  zoomMin: number;
  zoomMax: number;
}

export interface BrowserSelectOption {
  label: string;
  value: string;
  disabled: boolean;
  /** `<optgroup>` label, when the option sits in one. */
  group?: string;
}

export interface BrowserRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** CSS cursor keywords the server reports (anything else is mapped to "default"). */
export const BROWSER_CURSORS = [
  'default', 'pointer', 'text', 'vertical-text', 'wait', 'progress', 'crosshair', 'move', 'not-allowed', 'grab', 'grabbing',
  'help', 'copy', 'alias', 'cell', 'context-menu', 'no-drop', 'zoom-in', 'zoom-out', 'all-scroll', 'col-resize', 'row-resize',
  'n-resize', 'e-resize', 's-resize', 'w-resize', 'ne-resize', 'nw-resize', 'se-resize', 'sw-resize', 'ew-resize', 'ns-resize',
  'nesw-resize', 'nwse-resize', 'none',
] as const;
export type BrowserCursor = (typeof BROWSER_CURSORS)[number];

export type BrowserSessionEndReason = 'idle' | 'evicted' | 'shutdown' | 'browser_crashed';

export type BrowserServerMessage =
  /** First reply to `hello` (and again if the session is replaced under you). */
  | {
      v: 1;
      t: 'session';
      sessionId: string;
      /** True when this attached to a live session (tabs, scroll and page state intact). */
      resumed: boolean;
      tabs: BrowserTab[];
      activeTabId: string | null;
      limits: BrowserLimits;
      homeUrl: string;
      searchUrl: string;
    }
  | { v: 1; t: 'tab.open'; tab: BrowserTab; activate: boolean }
  /** Any field of a tab changed; always the full tab state. Coalesced, so it can lag by ~50 ms. */
  | { v: 1; t: 'tab.update'; tab: BrowserTab }
  | { v: 1; t: 'tab.closed'; tabId: string; activeTabId: string | null }
  | { v: 1; t: 'tab.active'; tabId: string }
  | { v: 1; t: 'tab.order'; tabIds: string[] }
  | { v: 1; t: 'cursor'; tabId: string; cursor: BrowserCursor }
  | {
      v: 1;
      t: 'ctx.result';
      requestId: string;
      tabId: string;
      linkUrl?: string;
      imageUrl?: string;
      mediaUrl?: string;
      selectionText: string;
      editable: boolean;
      pageUrl: string;
    }
  /** Text the page copied (or `copy.request` answered): put it on the user's clipboard. */
  | { v: 1; t: 'copy.text'; tabId: string; requestId?: string; text: string }
  /** The page opened a `<select>`; render a menu at `rect` and answer with `select.choose`. */
  | {
      v: 1;
      t: 'select.open';
      requestId: string;
      tabId: string;
      options: BrowserSelectOption[];
      selectedIndex: number;
      /** Position of the control in client viewport CSS pixels. */
      rect: BrowserRect;
    }
  | { v: 1; t: 'select.close'; requestId: string }
  | {
      v: 1;
      t: 'dialog.open';
      dialogId: string;
      tabId: string;
      kind: 'alert' | 'confirm' | 'prompt' | 'beforeunload';
      message: string;
      defaultValue: string;
      /** Without an answer the server dismisses the dialog after this long. */
      timeoutMs: number;
    }
  | { v: 1; t: 'dialog.closed'; dialogId: string }
  | {
      v: 1;
      t: 'filechooser.open';
      requestId: string;
      tabId: string;
      multiple: boolean;
      /** The input's `accept` entries (".pdf", "image/*", ...). */
      accept: string[];
      /** `webkitdirectory`: answer with the id of one uploaded folder. */
      directory: boolean;
      timeoutMs: number;
    }
  | { v: 1; t: 'filechooser.closed'; requestId: string }
  | { v: 1; t: 'downloads'; list: BrowserDownload[] }
  | { v: 1; t: 'download.update'; download: BrowserDownload }
  | { v: 1; t: 'download.removed'; id: string }
  | { v: 1; t: 'error'; code: BrowserErrorCode; message: string; tabId?: string; retryAfterSeconds?: number }
  /** Something the user should know but nothing failed. */
  | { v: 1; t: 'notice'; code: BrowserNoticeCode; message: string; tabId?: string }
  | { v: 1; t: 'session.ending'; reason: BrowserSessionEndReason }
  | { v: 1; t: 'pong'; ts?: number };

export type BrowserServerMessageType = BrowserServerMessage['t'];

export type BrowserErrorCode =
  | 'invalid_message'
  | 'unsupported_version'
  | 'expected_hello'
  | 'unauthorized'
  | 'guest_not_allowed'
  | 'feature_disabled'
  | 'browser_busy'
  | 'browser_unavailable'
  | 'memory_pressure'
  | 'rate_limited'
  | 'too_many_tabs'
  | 'unknown_tab'
  | 'blocked_url'
  | 'action_failed'
  | 'unknown_request'
  | 'too_many_downloads'
  | 'unknown_download';

export type BrowserNoticeCode =
  | 'blocked_destination'
  | 'popup_blocked'
  | 'download_started'
  | 'permission_denied'
  | 'dialog_timeout'
  | 'tab_restored';

export const BROWSER_ERROR_STATUS: Partial<Record<BrowserErrorCode, number>> = {
  browser_busy: 503,
  memory_pressure: 503,
  browser_unavailable: 503,
  feature_disabled: 404,
  guest_not_allowed: 403,
  unauthorized: 401,
  rate_limited: 429,
};

// ---------------------------------------------------------------------------------------------
// HTTP (/api/browser)
// ---------------------------------------------------------------------------------------------

/** `GET /api/browser/config`: lets the UI hide or disable the module without opening a socket. */
export interface BrowserConfigResponse {
  enabled: boolean;
  /** Guests may use it (BROWSER_ALLOW_GUEST). */
  guestAllowed: boolean;
  /** The server's Chromium is installed and executable; false means the module cannot work here. */
  available?: boolean;
  /** Present only when enabled. */
  limits?: BrowserLimits;
  homeUrl?: string;
  searchUrl?: string;
  /** The server has no free session slot for this user right now (a new `hello` would get `browser_busy`). */
  busy?: boolean;
  /** This user already has a live session to resume. */
  hasSession?: boolean;
  liveSessions?: number;
  maxSessions?: number;
}

/** A file waiting on the server to answer a file chooser with. */
export interface BrowserUpload {
  id: string;
  name: string;
  size: number;
  /** True for an uploaded folder (answers a `directory` chooser). */
  directory: boolean;
}

export const browserUploadFromFilesInput = z.object({
  fileIds: z.array(z.uuid()).min(1).max(100),
});
export type BrowserUploadFromFilesInput = z.infer<typeof browserUploadFromFilesInput>;

// ---------------------------------------------------------------------------------------------
// Binary frames
// ---------------------------------------------------------------------------------------------

export const FRAME_MIME = ['image/jpeg', 'image/png', 'image/webp'] as const;
export type FrameMime = (typeof FRAME_MIME)[number];

export interface BrowserFrame {
  tabId: string;
  /** Increases per tab; use it to drop out-of-order frames after a tab switch. */
  sequence: number;
  mime: FrameMime;
  /** Encoded image. A view into the message buffer; copy it if you keep it. */
  data: Uint8Array;
}

/**
 * Layout: u8 version (1) | u8 kind (1 = frame) | u8 mime index | u8 tabId length N | N bytes tabId (ASCII)
 * | u32 big-endian sequence | image bytes.
 */
export function encodeFrame(frame: BrowserFrame): Uint8Array {
  const id = new TextEncoder().encode(frame.tabId);
  if (id.length > 255) throw new RangeError('tabId too long');
  const out = new Uint8Array(8 + id.length + frame.data.length);
  out[0] = 1;
  out[1] = 1;
  out[2] = FRAME_MIME.indexOf(frame.mime);
  out[3] = id.length;
  out.set(id, 4);
  new DataView(out.buffer).setUint32(4 + id.length, frame.sequence >>> 0);
  out.set(frame.data, 8 + id.length);
  return out;
}

/** Null for anything that is not a version-1 frame. */
export function decodeFrame(buffer: ArrayBuffer | Uint8Array): BrowserFrame | null {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  if (bytes.length < 9 || bytes[0] !== 1 || bytes[1] !== 1) return null;
  const mime = FRAME_MIME[bytes[2]!];
  const idLength = bytes[3]!;
  if (!mime || bytes.length < 8 + idLength) return null;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  return {
    tabId: new TextDecoder().decode(bytes.subarray(4, 4 + idLength)),
    sequence: view.getUint32(4 + idLength),
    mime,
    data: bytes.subarray(8 + idLength),
  };
}

// ---------------------------------------------------------------------------------------------
// Address bar input
// ---------------------------------------------------------------------------------------------

const BLOCKED_SCHEMES = new Set(['javascript', 'data', 'file', 'chrome', 'chrome-extension', 'view-source', 'blob', 'about', 'ftp', 'devtools', 'filesystem']);
const SCHEME = /^([a-z][a-z0-9+.-]*):/i;
const BARE_HOST = /^(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9-]{2,}(?::\d{1,5})?(?:[/?#].*)?$/iu;
const LOCAL_HOST = /^(?:localhost|\[[0-9a-f:.]+\]|\d{1,3}(?:\.\d{1,3}){3})(?::\d{1,5})?(?:[/?#].*)?$/i;

export type NormalizedInput = { ok: true; url: string; search: boolean } | { ok: false; reason: 'empty' | 'too_long' | 'blocked_scheme' | 'invalid' };

/**
 * Turns what a user typed into a URL: an explicit http(s) address stays as typed, "host.tld/path" gets
 * https://, anything else becomes a search (`searchUrl` has `%s` for the encoded query). Only http and
 * https can result; `about:blank` is accepted verbatim for an empty tab. The server applies the same
 * function to every `nav`/`tab.new`, so the UI may use it for live hints but cannot bypass it.
 */
export function normalizeInput(raw: string, searchUrl: string): NormalizedInput {
  const text = raw.trim();
  if (!text) return { ok: false, reason: 'empty' };
  if (text.length > 8192) return { ok: false, reason: 'too_long' };
  if (text.toLowerCase() === 'about:blank') return { ok: true, url: 'about:blank', search: false };

  const scheme = SCHEME.exec(text)?.[1]?.toLowerCase();
  const hostPort = scheme !== undefined && /^[a-z0-9.-]+:\d{1,5}(?:[/?#]|$)/i.test(text);
  if (scheme && !hostPort) {
    if (scheme === 'http' || scheme === 'https') return toUrl(text, false);
    if (BLOCKED_SCHEMES.has(scheme) || /^[a-z][a-z0-9+.-]*:\/\//i.test(text)) return { ok: false, reason: 'blocked_scheme' };
  }

  if (/\s/.test(text)) return toSearch(text, searchUrl);
  if (BARE_HOST.test(text) || LOCAL_HOST.test(text) || hostPort) return toUrl(`https://${text}`, false);
  return toSearch(text, searchUrl);
}

function toUrl(candidate: string, search: boolean): NormalizedInput {
  try {
    const parsed = new URL(candidate);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return { ok: false, reason: 'blocked_scheme' };
    if (!parsed.hostname) return { ok: false, reason: 'invalid' };
    return { ok: true, url: parsed.href, search };
  } catch {
    return { ok: false, reason: 'invalid' };
  }
}

function toSearch(text: string, searchUrl: string): NormalizedInput {
  return toUrl(searchUrl.replace('%s', encodeURIComponent(text)), true);
}
